const STORAGE_KEY = 'auth_return_to_v1';

export type AuthReturnRoute = {
  pathname: string;
  search?: string;
  hash?: string;
  label?: string;
  createdAt: number;
};

const MAX_AGE_MS = 1000 * 60 * 60 * 4;

const STATIC_LABELS: Array<[RegExp, string]> = [
  [/^\/marketplace($|\/)/i, 'Marketplace'],
  [/^\/checkout($|\/)/i, 'Checkout'],
  [/^\/dashboard($|\/)/i, 'Dashboard'],
  [/^\/community($|\/)/i, 'Community Hub'],
  [/^\/blog($|\/)/i, 'Blog'],
  [/^\/county($|\/)/i, 'County Info'],
  [/^\/import-export($|\/)/i, 'Import / Export Analysis'],
  [/^\/farm($|\/)/i, 'Farm Management'],
  [/^\/services($|\/)/i, 'Services'],
  [/^\/about($|\/)/i, 'About'],
  [/^\/contact($|\/)/i, 'Contact'],
  [/^\/product($|\/)/i, 'Product Details'],
  [/^\/shop($|\/)/i, 'Shop Page'],
  [/^\/seller($|\/)/i, 'Seller Page'],
  [/^\/tracking($|\/)/i, 'Order Tracking'],
];

const DEFAULT_DASHBOARD = '/dashboard';

const isBrowser = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

export const isSafeInternalPath = (candidate: unknown): candidate is { pathname: string; search?: string; hash?: string } => {
  if (!candidate || typeof candidate !== 'object') return false;
  const route = candidate as { pathname?: unknown; search?: unknown; hash?: unknown };
  const pathname = typeof route.pathname === 'string' ? route.pathname : '';
  const search = typeof route.search === 'string' ? route.search : '';
  const hash = typeof route.hash === 'string' ? route.hash : '';

  if (!pathname) return false;
  if (!pathname.startsWith('/')) return false;
  if (pathname.startsWith('//')) return false;
  if (/^https?:\/\//i.test(pathname)) return false;

  const lowered = pathname.toLowerCase();
  if (
    lowered.startsWith('/login') ||
    lowered.startsWith('/register') ||
    lowered.startsWith('/verify-email') ||
    lowered.startsWith('/reset-password') ||
    lowered.startsWith('/logout')
  ) {
    return false;
  }

  const combined = `${pathname}${search}${hash}`;
  if (combined.length > 8192) return false;

  return true;
};

export const toPathString = (route: { pathname: string; search?: string; hash?: string }): string => {
  const p = route.pathname.startsWith('/') ? route.pathname : DEFAULT_DASHBOARD;
  const s = typeof route.search === 'string' && route.search && !route.search.startsWith('?') ? `?${route.search}` : (route.search || '');
  const h = typeof route.hash === 'string' && route.hash && !route.hash.startsWith('#') ? `#${route.hash}` : (route.hash || '');
  return `${p}${s}${h}`;
};

export const deriveLabel = (route: { pathname: string }): string => {
  for (const [re, label] of STATIC_LABELS) {
    if (re.test(route.pathname)) return label;
  }
  if (/^\/($|\?|#)/.test(route.pathname)) return 'Home';
  try {
    const last = route.pathname.split('/').filter(Boolean).slice(-1)[0];
    if (last) return decodeURIComponent(last).replace(/[-_]+/g, ' ');
  } catch {
    /* ignore */
  }
  return 'the page you were viewing';
};

export const saveAuthReturnRoute = (
  input: { pathname: string; search?: string; hash?: string } | Location | { from: Location | { pathname: string; search?: string; hash?: string } }
): AuthReturnRoute | null => {
  if (!isBrowser()) return null;

  let route: { pathname: string; search?: string; hash?: string } | null = null;
  let label: string | undefined;

  if (input && typeof input === 'object') {
    const anyInput = input as any;
    if (anyInput.from) {
      const from = anyInput.from as any;
      if (typeof from === 'object') {
        if (typeof from.pathname === 'string') {
          route = { pathname: from.pathname, search: from.search || '', hash: from.hash || '' };
        } else if (from && typeof from === 'object') {
          route = { pathname: from.pathname, search: from.search || '', hash: from.hash || '' };
        }
      }
    } else if (typeof anyInput.pathname === 'string') {
      route = { pathname: anyInput.pathname, search: anyInput.search || '', hash: anyInput.hash || '' };
    }
  }

  if (!route || !isSafeInternalPath(route)) return null;

  const normalized: AuthReturnRoute = {
    pathname: route.pathname,
    search: route.search || '',
    hash: route.hash || '',
    label: label ?? deriveLabel(route),
    createdAt: Date.now(),
  };

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    return null;
  }
  return normalized;
};

export const getAuthReturnRoute = (): AuthReturnRoute | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthReturnRoute;
    if (!isSafeInternalPath(parsed)) return null;
    if (typeof parsed.createdAt === 'number' && Date.now() - parsed.createdAt > MAX_AGE_MS) return null;
    return {
      pathname: parsed.pathname,
      search: typeof parsed.search === 'string' ? parsed.search : '',
      hash: typeof parsed.hash === 'string' ? parsed.hash : '',
      label: typeof parsed.label === 'string' && parsed.label ? parsed.label : deriveLabel(parsed),
      createdAt: typeof parsed.createdAt === 'number' ? parsed.createdAt : Date.now(),
    };
  } catch {
    return null;
  }
};

export const consumeAuthReturnRoute = (): AuthReturnRoute | null => {
  const value = getAuthReturnRoute();
  if (isBrowser()) {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  return value;
};

export const clearAuthReturnRoute = (): void => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const getDashboardFallback = (): string => DEFAULT_DASHBOARD;
